import { getMomentTz, MESApiService, spParameter } from "@dexteel/mesf-core";
import { get } from "lodash-es";
import type { Campaign } from "@/pages/mill/schedule/models/Campaign";
import type {
	CampaignDelayResponse,
	CampaignResponse,
} from "@/pages/mill/schedule/models/CampaignReponse";
import type { HeatMechanicalResult } from "@/pages/mill/schedule/models/HeatMechanicalResult";

const moment = getMomentTz();

type GetCampaignsParams = {
	campaignId?: number | null;
	start?: Date | null;
	end?: Date | null;
};

export const getM1Campaigns = async (
	params?: GetCampaignsParams,
	signal?: AbortSignal,
) => {
	const apiService = new MESApiService();

	const parameters: spParameter[] = [];

	if (params?.campaignId)
		parameters.push({ name: "CampaignId", value: params.campaignId });
	if (params?.start)
		parameters.push({ name: "Start", value: params.start.toISOString() });
	if (params?.end)
		parameters.push({ name: "End", value: params.end.toISOString() });

	const resp = await apiService.callV2(
		"[M1].[GetCampaigns]",
		parameters,
		signal,
	);

	if (resp.ok) {
		let rows = get(resp, "data.tables[0].rows", []);

		rows = rows.map((campaign: CampaignResponse) => ({
			...campaign,
			Start: campaign.Start ? moment.utc(campaign["Start"]).toDate() : null,
			End: campaign.End ? moment.utc(campaign["End"]).toDate() : null,
			SampleStatusTimestamp: campaign.SampleStatusTimestamp
				? moment.utc(campaign["SampleStatusTimestamp"]).toDate()
				: null,
		}));

		return rows as CampaignResponse[];
	} else {
		throw new Error(resp.message || "Error fetching M1 campaigns");
	}
};

export const getM1CampaignDelays = async (
	params: {
		start?: Date;
		end?: Date;
	},
	signal?: AbortSignal,
) => {
	const apiService = new MESApiService();

	const parameters: spParameter[] = [
		{ name: "Start", value: params.start?.toISOString() ?? null },
		{ name: "End", value: params.end?.toISOString() ?? null },
	];

	const resp = await apiService.callV2(
		"[M1].[GetCampaignDelays]",
		parameters,
		signal,
	);

	if (resp.ok) {
		return get(resp, "data.tables[0].rows", []) as CampaignDelayResponse[];
	} else {
		throw new Error(resp.message || "Error fetching M1 campaign delays");
	}
};

export const upsertM1Campaign = async (campaign: Campaign) => {
	const apiService = new MESApiService();

	const parameters: spParameter[] = [
		{ name: "CampaignId", value: campaign.CampaignId },
		{ name: "CampaignNumber", value: campaign.CampaignNumber },
		{ name: "SectionId", value: campaign.SectionId },
		{ name: "HeatId", value: campaign.HeatId },
		{ name: "Start", value: campaign.Start.toISOString() },
		{ name: "End", value: campaign.End?.toISOString() ?? null },
		{ name: "ChargedBillets", value: campaign.ChargedBillets },
		{ name: "RolledWeightCold", value: campaign.RolledColdTons },
		{ name: "RolledWeightHot", value: campaign.RolledHotTons },
	];

	const resp = await apiService.callV2("[M1].[UpsertCampaign]", parameters);

	if (resp.ok) {
		return get(resp, "data.returnValue", null) as number;
	} else {
		throw new Error(resp.message || "Error upserting M1 campaign");
	}
};

export const deleteM1Campaign = async (campaignId: number) => {
	const apiService = new MESApiService();

	const parameters: spParameter[] = [{ name: "CampaignId", value: campaignId }];

	const resp = await apiService.callV2("[M1].[DeleteCampaign]", parameters);

	if (!resp.ok) {
		throw new Error(resp.message || "Error deleting M1 campaign");
	}
};

export const getM1HeatMechanicalResults = async (
	params: { heatId: number },
	signal?: AbortSignal,
) => {
	const apiService = new MESApiService();

	const parameters: spParameter[] = [{ name: "HeatId", value: params.heatId }];

	const resp = await apiService.callV2(
		"[LAB].[GetHeatMechanicalResults]",
		parameters,
		signal,
	);

	if (resp.ok) {
		return get(resp, "data.tables[0].rows", []) as HeatMechanicalResult[];
	} else {
		throw new Error(
			resp.message || "Error fetching M1 heat mechanical results",
		);
	}
};
